import React, {useMemo} from 'react';
import {loadExternalComponent, usePlugin} from '../../PluginContext.tsx';
import {useTranslation} from 'react-i18next';
import * as el from '../../../ducks/entitylist';
import {useEntityDetail} from '../../../ducks/entitydetail.js';
import {patchEntity, createEntity} from '../../../ducks/data.js';
import {Alert} from '../../../ui-toolkit/index';
import {SpinnerLoader} from '../../../ui-toolkit/animation-v2/loading-spinner/LoadingSpinner';

/**
 * Wrapper around loadExternalComponent to facilitate lazy loading
 */
export function loadComponent(scope, module) {
    return async () => loadExternalComponent(scope, module);
}

class PluginErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {hasError: false};
    }

    static getDerivedStateFromError() {
        return {hasError: true};
    }

    render() {
        if (this.state.hasError) {
            return <Alert>{this.props.t('pluginFormatter.pluginCrashed')}</Alert>;
        }

        return this.props.children;
    }
}

function PluginFormatter(props) {
    const {formatter} = props;
    const pluginConfig = formatter.settings.pluginConfig;
    const {ready, failed} = usePlugin(pluginConfig);
    const {t} = useTranslation();

    if (!ready) {
        return <SpinnerLoader isLoading center="page" />;
    }

    if (failed) {
        return <Alert>{t('pluginFormatter.failedLoading')}</Alert>;
    }

    return (
        <PluginErrorBoundary t={t}>
            <Plugin {...props} pluginConfig={pluginConfig} el={el} />
        </PluginErrorBoundary>
    );
}

function Plugin(props) {
    const {pluginConfig, el} = props;

    // TODO: the "entity loader" is deprecated, get rid of this if no plugin is using it anymore
    const memoizedEl = useMemo(() => {
        const loadEntities = (props) => {
            return el.loadEntities({
                ...props,
            });
        };

        return {
            ...el,
            loadEntities,
            exportToExcel: null,
        };
    }, []);

    const Component = React.useMemo(
        () => React.lazy(loadComponent(pluginConfig.scope, pluginConfig.module)),
        [pluginConfig.scope, pluginConfig.module],
    );

    return (
        <React.Suspense fallback={<SpinnerLoader isLoading center="page" />}>
            <Component
                {...props}
                el={memoizedEl}
                useEntityDetail={useEntityDetail}
                patchEntity={patchEntity}
                createEntity={createEntity}
            />
        </React.Suspense>
    );
}

export default PluginFormatter;
